// ─────────────────────────────────────────────────────────────────────────────
// Auto-hiding native scrollbars, everywhere, with one listener.
//
// CSS can hide a bar at rest and show it on hover or focus, but it cannot
// tell that a container is being scrolled. This marks the element that is
// scrolling with `rr-scrolling` for a moment after each movement, and the
// stylesheet reveals its bar for as long as the class is there. One capturing
// listener on the document covers every scroll container in the app, present
// and future, with no per-element setup and nothing to keep in step.
// ─────────────────────────────────────────────────────────────────────────────

export const SCROLLING_CLASS = 'rr-scrolling';

/** How long after the last movement the bar stays, in ms. Matches Glide's `scrollingThreshold`. */
export const SCROLL_REVEAL_MS = 700;

export function installScrollReveal(doc: Document = document, linger = SCROLL_REVEAL_MS): () => void {
  const timers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

  const onScroll = (e: Event) => {
    // The document scrolls as `document`; everything else as itself.
    const target = e.target === doc ? doc.documentElement : e.target;
    if (!(target instanceof Element)) return;
    if (!target.classList.contains(SCROLLING_CLASS)) target.classList.add(SCROLLING_CLASS);
    const pending = timers.get(target);
    if (pending) clearTimeout(pending);
    timers.set(target, setTimeout(() => {
      target.classList.remove(SCROLLING_CLASS);
      timers.delete(target);
    }, linger));
  };

  doc.addEventListener('scroll', onScroll, { capture: true, passive: true });
  return () => doc.removeEventListener('scroll', onScroll, { capture: true });
}
